/**
 * Button System — Enterprise Design System
 *
 * Thin wrappers around MUI Button providing pre-configured variants.
 * Sizes (small/medium/large) and focus rings are defined in componentOverrides.js.
 *
 * Exports:
 *   PrimaryButton, SecondaryButton, OutlinedButton, DangerButton,
 *   SuccessButton, GhostButton, IconBtn, FAB, LoadingButton, SplitButton
 */

import React, { useState, useRef, useCallback } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// ── PrimaryButton ─────────────────────────────────────────────────────────────
/**
 * Contained button with primary color.
 * Passes all props through to MUI Button.
 */
export const PrimaryButton = React.forwardRef(function PrimaryButton(props, ref) {
  const { disabled, ...rest } = props;
  return (
    <Button
      ref={ref}
      variant="contained"
      color="primary"
      aria-disabled={disabled || undefined}
      disabled={disabled}
      sx={disabled ? { opacity: 0.38, ...rest.sx } : rest.sx}
      {...rest}
    />
  );
});

// ── SecondaryButton ───────────────────────────────────────────────────────────
/**
 * Contained button with secondary color.
 */
export const SecondaryButton = React.forwardRef(function SecondaryButton(props, ref) {
  const { disabled, ...rest } = props;
  return (
    <Button
      ref={ref}
      variant="contained"
      color="secondary"
      aria-disabled={disabled || undefined}
      disabled={disabled}
      sx={disabled ? { opacity: 0.38, ...rest.sx } : rest.sx}
      {...rest}
    />
  );
});

// ── OutlinedButton ────────────────────────────────────────────────────────────
/**
 * Outlined button with default (primary) color.
 */
export const OutlinedButton = React.forwardRef(function OutlinedButton(props, ref) {
  const { disabled, ...rest } = props;
  return (
    <Button
      ref={ref}
      variant="outlined"
      aria-disabled={disabled || undefined}
      disabled={disabled}
      sx={disabled ? { opacity: 0.38, ...rest.sx } : rest.sx}
      {...rest}
    />
  );
});

// ── DangerButton ──────────────────────────────────────────────────────────────
/**
 * Contained button with error (danger) color.
 */
export const DangerButton = React.forwardRef(function DangerButton(props, ref) {
  const { disabled, ...rest } = props;
  return (
    <Button
      ref={ref}
      variant="contained"
      color="error"
      aria-disabled={disabled || undefined}
      disabled={disabled}
      sx={disabled ? { opacity: 0.38, ...rest.sx } : rest.sx}
      {...rest}
    />
  );
});

// ── SuccessButton ─────────────────────────────────────────────────────────────
/**
 * Contained button with success color.
 */
export const SuccessButton = React.forwardRef(function SuccessButton(props, ref) {
  const { disabled, ...rest } = props;
  return (
    <Button
      ref={ref}
      variant="contained"
      color="success"
      aria-disabled={disabled || undefined}
      disabled={disabled}
      sx={disabled ? { opacity: 0.38, ...rest.sx } : rest.sx}
      {...rest}
    />
  );
});

// ── GhostButton ───────────────────────────────────────────────────────────────
/**
 * Text-only button with no background — minimal visual weight.
 * Uses MUI's "text" variant with subtle hover state.
 */
export const GhostButton = React.forwardRef(function GhostButton(props, ref) {
  const { disabled, sx, ...rest } = props;
  return (
    <Button
      ref={ref}
      variant="text"
      aria-disabled={disabled || undefined}
      disabled={disabled}
      sx={{
        color: 'text.secondary',
        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        ...(disabled ? { opacity: 0.38 } : {}),
        ...sx,
      }}
      {...rest}
    />
  );
});

// ── IconBtn ───────────────────────────────────────────────────────────────────
/**
 * MUI IconButton with consistent sizing.
 * Sizes: small (30px), medium (36px), large (42px).
 */
const ICON_BTN_SIZES = {
  small: 30,
  medium: 36,
  large: 42,
};

export const IconBtn = React.forwardRef(function IconBtn(props, ref) {
  const { size = 'medium', disabled, sx, ...rest } = props;
  const dimension = ICON_BTN_SIZES[size] || ICON_BTN_SIZES.medium;

  return (
    <IconButton
      ref={ref}
      size={size}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      sx={{
        width: dimension,
        height: dimension,
        ...(disabled ? { opacity: 0.38 } : {}),
        ...sx,
      }}
      {...rest}
    />
  );
});

// ── FAB ───────────────────────────────────────────────────────────────────────
/**
 * Floating Action Button with circular shape (50% border-radius).
 */
export const FAB = React.forwardRef(function FAB(props, ref) {
  const { disabled, sx, ...rest } = props;
  return (
    <Fab
      ref={ref}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      sx={{
        borderRadius: '50%',
        ...(disabled ? { opacity: 0.38 } : {}),
        ...sx,
      }}
      {...rest}
    />
  );
});

// ── LoadingButton ─────────────────────────────────────────────────────────────
/**
 * Button that shows a CircularProgress spinner when loading=true.
 * Disables interaction while loading.
 */
const SPINNER_SIZES = {
  small: 13,
  medium: 14,
  large: 15,
};

export const LoadingButton = React.forwardRef(function LoadingButton(props, ref) {
  const {
    loading = false,
    children,
    disabled,
    size = 'medium',
    startIcon,
    endIcon,
    sx,
    ...rest
  } = props;

  const isDisabled = disabled || loading;
  const spinnerSize = SPINNER_SIZES[size] || SPINNER_SIZES.medium;

  return (
    <Button
      ref={ref}
      size={size}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      startIcon={loading ? undefined : startIcon}
      endIcon={loading ? undefined : endIcon}
      sx={{
        ...(isDisabled ? { opacity: 0.38 } : {}),
        ...sx,
      }}
      {...rest}
    >
      {loading ? (
        <CircularProgress
          size={spinnerSize}
          color="inherit"
          aria-label="Loading"
        />
      ) : (
        children
      )}
    </Button>
  );
});

// ── SplitButton ───────────────────────────────────────────────────────────────
/**
 * Split button with primary action + dropdown for secondary actions.
 *
 * Props:
 *   - label: string — primary button text
 *   - onClick: () => void — primary action handler
 *   - options: Array<{ label: string, onClick: () => void, icon?: ReactNode, disabled?: boolean }>
 *   - variant: 'contained' | 'outlined' (default: 'contained')
 *   - color: MUI color (default: 'primary')
 *   - disabled: boolean
 *   - size: 'small' | 'medium' | 'large'
 */
export function SplitButton({
  label,
  onClick,
  options = [],
  variant = 'contained',
  color = 'primary',
  disabled = false,
  size = 'medium',
  startIcon,
  sx,
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleMenuItemClick = useCallback((option) => {
    setOpen(false);
    if (option.onClick) option.onClick();
  }, []);

  return (
    <>
      <ButtonGroup
        variant={variant}
        color={color}
        ref={anchorRef}
        aria-label="split button"
        disabled={disabled}
        size={size}
        sx={sx}
      >
        <Button
          onClick={onClick}
          startIcon={startIcon}
          aria-label={label}
        >
          {label}
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{ px: 0.5, minWidth: 'unset' }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Menu
        id="split-button-menu"
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 0.5, minWidth: 180 } }}
      >
        {options.map((option, index) => (
          <MenuItem
            key={option.label || index}
            disabled={option.disabled}
            onClick={() => handleMenuItemClick(option)}
          >
            {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
